import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { FaPhoneAlt } from 'react-icons/fa';
import { BackgroundBeams } from '@/components/ui/background-beams';
import type { CTASectionData, ContactInfoData } from '@/sanity/types';

const WHATSAPP_URL = 'https://wa.me/?text=Hola%20INBOCA%2C%20quiero%20transformar%20mi%20espacio%20corporativo';

interface CTASectionProps {
  data?: CTASectionData | undefined;
  contactInfo?: ContactInfoData | undefined;
}

/**
 * R2 - Premium CTA section with animated background
 * R4 - Accepts optional Sanity data, falls back to hardcoded defaults
 */
export function CTASection({ data, contactInfo }: CTASectionProps) {
  const badge = data?.badge ?? '¿Listo para el cambio?';
  const headingLine1 = data?.headingLine1 ?? 'Transforma tu espacio';
  const headingLine2 = data?.headingLine2 ?? 'en una experiencia única';
  const description = data?.description ?? 'Más de 500 empresas ya confían en INBOCA para crear ambientes que inspiran productividad y bienestar.';
  const ctaPrimary = data?.ctaPrimaryText ?? 'Solicitar Presupuesto';
  const ctaSecondary = data?.ctaSecondaryText ?? 'Llamar Ahora';

  const whatsappUrl = contactInfo
    ? `https://wa.me/${contactInfo.whatsappNumber}?text=${encodeURIComponent(contactInfo.whatsappDefaultMessage)}`
    : WHATSAPP_URL;

  const indicators = data?.trustIndicators ?? [
    { value: '10+', label: 'Años de experiencia', colorVariant: 'teal' as const },
    { value: '500+', label: 'Proyectos completados', colorVariant: 'orange' as const },
    { value: '98%', label: 'Clientes satisfechos', colorVariant: 'teal' as const },
  ];
  return (
    <section className="relative h-[32rem] sm:h-[36rem] w-full bg-slate-50 flex items-center justify-center overflow-hidden">
      {/* Animated Background */}
      <BackgroundBeams />

      {/* Content */}
      <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        {/* Badge */}
        <span className="inline-block px-4 py-2 bg-inboca-teal/10 rounded-full text-inboca-teal font-semibold text-sm mb-8">
          {badge}
        </span>

        {/* Headline */}
        <h2 className="text-2xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-slate-900 mb-6 leading-tight">
          {headingLine1}
          <span className="block text-inboca-teal">{headingLine2}</span>
        </h2>

        {/* Subheadline */}
        <p className="text-lg sm:text-xl text-slate-600 max-w-2xl mx-auto mb-10 leading-relaxed">
          {description}
        </p>

        {/* CTA Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
          <Link
            to="/contacto"
            className="inline-flex items-center justify-center gap-2 bg-inboca-teal hover:bg-inboca-teal/90 text-white px-8 py-4 rounded-lg font-bold text-lg transition-all duration-300 shadow-lg shadow-inboca-teal/20 hover:shadow-xl hover:shadow-inboca-teal/30"
          >
            {ctaPrimary}
            <ArrowRight className="h-5 w-5" />
          </Link>
          <a
            href={whatsappUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center gap-2 bg-white hover:bg-slate-50 text-slate-700 border-2 border-slate-200 hover:border-inboca-orange px-8 py-4 rounded-lg font-bold text-lg transition-all duration-300 shadow-sm hover:shadow-md"
          >
            <FaPhoneAlt className="h-4 w-4" />
            {ctaSecondary}
          </a>
        </div>

        {/* Trust Indicators */}
        <div className="mt-12 flex flex-wrap justify-center gap-8 text-slate-500 text-sm">
          {indicators.map((ind) => (
            <div key={ind.label} className="flex items-center gap-2">
              <span className={`text-2xl font-bold ${
                ind.colorVariant === 'orange' ? 'text-inboca-orange' : 'text-inboca-teal'
              }`}>{ind.value}</span>
              <span>{ind.label}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
